// ── Admin location storage ──
const ADMIN_LOCATION_KEY = 'admin_service_location';
const SERVICE_RADIUS_KM = 10;
const STORAGE_KEY = 'clothybazar_location_status';
const OUT_OF_AREA_VISITORS_KEY = 'out_of_area_visitors';

export interface AdminServiceLocation {
  lat: number;
  lng: number;
  address: string;
  setAt: number;
}

// Default fallback (Dehradun center) if admin hasn't set location
const DEFAULT_CENTER = { lat: 30.3165, lng: 78.0322 };

export function getAdminServiceLocation(): AdminServiceLocation | null {
  try {
    const raw = localStorage.getItem(ADMIN_LOCATION_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as AdminServiceLocation;
  } catch { return null; }
}

export function saveAdminServiceLocation(loc: Omit<AdminServiceLocation, 'setAt'>): void {
  try {
    localStorage.setItem(ADMIN_LOCATION_KEY, JSON.stringify({ ...loc, setAt: Date.now() }));
    // Clear cached customer location checks so they re-check against new center
    localStorage.removeItem(STORAGE_KEY);
  } catch { /* ignore */ }
}

export function getServiceCenter(): { lat: number; lng: number } {
  const adminLoc = getAdminServiceLocation();
  return adminLoc ? { lat: adminLoc.lat, lng: adminLoc.lng } : DEFAULT_CENTER;
}

export interface LocationStatus {
  checked: boolean;
  inArea: boolean;
  lat?: number;
  lng?: number;
  city?: string;
  address?: string;
  timestamp?: number;
  distanceKm?: number;
}

export interface OutOfAreaVisitor {
  id: string;
  name: string;
  whatsapp: string;
  lat: number;
  lng: number;
  city: string;
  address: string;
  distanceKm: number;
  timestamp: number;
  userAgent?: string;
}

// Haversine formula to calculate distance between two coordinates in km
export function calcDistanceKm(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

export function isInsideServiceArea(lat: number, lng: number): boolean {
  const center = getServiceCenter();
  const dist = calcDistanceKm(center.lat, center.lng, lat, lng);
  return dist <= SERVICE_RADIUS_KM;
}

export function getSavedLocationStatus(): LocationStatus | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed: LocationStatus = JSON.parse(raw);
    // Expire after 30 minutes
    if (parsed.timestamp && Date.now() - parsed.timestamp > 30 * 60 * 1000) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch { return null; }
}

export function saveLocationStatus(status: LocationStatus): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...status, timestamp: Date.now() }));
  } catch { /* ignore */ }
}

export function saveOutOfAreaVisitor(visitor: Omit<OutOfAreaVisitor, 'id' | 'timestamp'>): void {
  try {
    const existing: OutOfAreaVisitor[] = JSON.parse(
      localStorage.getItem(OUT_OF_AREA_VISITORS_KEY) ?? '[]'
    );
    // Avoid duplicate by whatsapp + same day
    const today = new Date().toDateString();
    const duplicate = existing.find(
      v => v.whatsapp === visitor.whatsapp &&
        new Date(v.timestamp).toDateString() === today
    );
    if (duplicate) return;

    const newVisitor: OutOfAreaVisitor = {
      ...visitor,
      id: 'oa-' + Date.now(),
      timestamp: Date.now(),
      userAgent: navigator.userAgent.substring(0, 100),
    };
    // Keep max 200 entries
    const updated = [newVisitor, ...existing].slice(0, 200);
    localStorage.setItem(OUT_OF_AREA_VISITORS_KEY, JSON.stringify(updated));
  } catch { /* ignore */ }
}

export function getOutOfAreaVisitors(): OutOfAreaVisitor[] {
  try {
    return JSON.parse(localStorage.getItem(OUT_OF_AREA_VISITORS_KEY) ?? '[]');
  } catch { return []; }
}
